import React, { useEffect } from "react";
import { listAllUsers } from "../api/admin/users";
import Loader from "../components/common/loader/loader";
import { TableColumn } from "../interfaces/tables";
import Table from "../components/ui/table";
import { useAuth } from "../contexts/auth.context";
import { siteSettings } from "../settings/site-settings";
import Error from "../components/common/error/error";
import TableHeaders from "../components/ui/table-headers";
import { Button } from "../components/ui/button";
import usePageTitle from "../hooks/usePageTitle";

function Users() {
  usePageTitle("Users");

  const { userProfile } = useAuth();

  const [users, setUsers] = React.useState([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.users.defaultPageSize.key
  );

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  const fetchUsers = async () => {
    const response = await listAllUsers({
      page,
      sort,
      search,
      pageSize,
    });

    if (!response || response.status !== 200) {
      console.log("Error fetching users");
      setError(true);
      setLoading(false);
      return;
    }

    setUsers(response.data.data);
    setTableData(formatDataForTable(response.data.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  // headers change listener
  useEffect(() => {
    fetchUsers();
  }, [page, sort, search, pageSize]);

  const columns: TableColumn[] = [
    { key: "_id", label: "Internal Id" },
    {
      key: "email",
      label: "Email",

      render: (cell, row) => {
        return row._id === userProfile?._id ? (
          <span>
            {cell} <span className="text-primary font-bold">(You)</span>{" "}
          </span>
        ) : (
          <span>{cell}</span>
        );
      },
    },
    {
      key: "role",
      label: "Role",
      render: (cell) => <span className="capitalize">{cell}</span>,
    },
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    {
      key: "isUserDisabled",
      label: "Disabled",
      render: (cell) => {
        return cell ? (
          <span className="text-red-500 uppercase">Yes</span>
        ) : (
          <span className="text-green-500 uppercase">No</span>
        );
      },
    },
    {
      key: "joinedAt",
      label: "Joined At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },
    // {
    //   key: "btn",
    //   label: "Action",
    //   render: (cell, row) => {
    //     return (
    //       <MdEdit
    //         title="Edit"
    //         className="hover:text-primary cursor-pointer"
    //         size={24}
    //         onClick={() => {
    //           alert(cell);
    //         }}
    //       />
    //     );
    //   },
    // },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        email: d.email,
        role: d.role,
        firstName: d.firstName ? d.firstName : "N/A",
        lastName: d.lastName ? d.lastName : "N/A",
        joinedAt: d.createdAt,
        isUserDisabled: d.isUserDisabled,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">Users</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <Button disabled={true}>Create User</Button>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.users.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.users.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.users.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.users.defaultPageSize}
            />
            <Table columns={columns} data={tableData} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
